import { CommonPagerInput, GQL_ID, PageInfo, Product } from '@@types'
import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { SHORTCUT_PRODUCT_CARD_FIELDS, SHORTCUT_PRODUCT_FIELDS, SHORTCUT_PRODUCT_SHOP_INFO } from '@shared/api/shortcuts'
import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'

export interface PopularProductsData {
  popularProducts: {
    pageInfo: PageInfo
    edges: Product[]
  }
}

export interface PopularProductsVariables {
  shop_id: GQL_ID
  style?: string
  pager?: CommonPagerInput
}

const GQL_QUERY_POPULAR_PRODUCTS = gql`
  query popularProducts ($shop_id: ID!, $style: String, $pager: CommonPagerInput) {
    popularProducts(shop_id: $shop_id, pager: $pager) {
      edges {
        ${SHORTCUT_PRODUCT_FIELDS}
        ${SHORTCUT_PRODUCT_SHOP_INFO}
        ${SHORTCUT_PRODUCT_CARD_FIELDS}
      }
      pageInfo {
        page
        perPage
        lastPage
        next
      }
    }
  }
`

export const usePopularProducts = (settings: PopularProductsVariables) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<PopularProductsData, PopularProductsVariables>(
    GQL_QUERY_POPULAR_PRODUCTS, {
      variables: settings,
      skip: !settings.shop_id,
      fetchPolicy: 'cache-and-network'
    }
  )

  const { hasMore, loadingMore, fetchNextPage } =
    useFetchMorePagination<
      PopularProductsData,
      PopularProductsVariables
    >({
      loading,
      fetchMore,
      pageVariable: 'pager',
      usePager: true,
      variables: {
        shop_id: settings.shop_id,
        pager: { limit: settings.pager?.limit ?? 10 }
      },
      updaterFunction (cached, fetched) {
        const prev = cached?.popularProducts?.edges ?? []
        const curr = fetched?.popularProducts?.edges ?? []

        return {
          hasMore: curr.length > 0,
          result: {
            ...cached,
            popularProducts: {
              ...cached.popularProducts,
              edges: [ ...prev, ...curr ],
            },
          }
        }
      },
    })


  const popularProducts = useMemo(() => data?.popularProducts?.edges ?? [], [ data ])
  const pageInfo = data?.popularProducts?.pageInfo

  return {
    popularProducts,
    pageInfo,
    data,
    loading,
    error,
    refetch,
    fetchNextPage,
    loadingMore,
    hasMore,
    fetchMore
  }
}
